/**
 * Global command palette — ⌘K to open, fuzzy search, section grouping.
 *
 * Built on `cmdk` (Radix-based, a11y-first). State + action registry lives
 * in `@/lib/commandPalette/registry`. Mount this component once near the
 * root of the app tree (inside <CommandPaletteProvider>).
 */

import { Command } from 'cmdk';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  type CommandAction,
  type CommandSectionId,
  useCommandPalette,
} from '@/lib/commandPalette/registry';

// ─── Section labels ─────────────────────────────────────────────────────────

const SECTION_LABELS: Record<CommandSectionId, string> = {
  navigate: 'Navigate',
  reports:  'Reports',
  actions:  'Actions',
  recent:   'Recent',
};

// Render order — sections appear in this sequence.
const SECTION_ORDER: CommandSectionId[] = ['recent', 'reports', 'actions', 'navigate'];

// ─── Component ──────────────────────────────────────────────────────────────

export function CommandPalette() {
  const { isOpen, close, actions } = useCommandPalette();
  const reduce = useReducedMotion() ?? false;
  const [search, setSearch] = useState('');

  // Reset search when palette closes.
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => setSearch(''), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Escape-to-close (WCAG 2.1.2 Focusable components requirement).
  useEffect(() => {
    if (!isOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, close]);

  // Group actions by section for rendering.
  const grouped = useMemo(() => {
    const map = new Map<CommandSectionId, CommandAction[]>();
    for (const action of actions) {
      const list = map.get(action.section) ?? [];
      list.push(action);
      map.set(action.section, list);
    }
    return map;
  }, [actions]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-start justify-center pt-[12vh] px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.1 : 0.15 }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={close}
            aria-hidden="true"
          />

          {/* Palette */}
          <motion.div
            className="relative w-full max-w-xl rounded-glass-lg glass-card overflow-hidden shadow-glass-xl"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -12, scale: 0.98 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: reduce ? 0.1 : 0.2, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <Command
              label="Command palette"
              shouldFilter
              className="flex flex-col max-h-[60vh]"
            >
              {/* Search input */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.08]">
                <SearchGlyph />
                <Command.Input
                  value={search}
                  onValueChange={setSearch}
                  placeholder="Search reports, commands, pages…"
                  className="flex-1 bg-transparent text-white placeholder:text-gray-500 text-sm focus:outline-none"
                  autoFocus
                />
                <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded border border-white/[0.15] text-[10px] font-mono text-gray-400">
                  ESC
                </kbd>
              </div>

              {/* Results */}
              <Command.List className="flex-1 overflow-y-auto py-2">
                <Command.Empty className="px-4 py-8 text-center text-sm text-gray-400">
                  No commands match{search ? ` “${search}”` : ''}.
                </Command.Empty>

                {SECTION_ORDER.map((section) => {
                  const items = grouped.get(section);
                  if (!items || items.length === 0) return null;
                  return (
                    <Command.Group
                      key={section}
                      heading={SECTION_LABELS[section]}
                      className="px-2 py-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-gray-500"
                    >
                      {items.map((action) => (
                        <PaletteItem
                          key={action.id}
                          action={action}
                          onRun={() => {
                            action.onSelect();
                            close();
                          }}
                        />
                      ))}
                    </Command.Group>
                  );
                })}
              </Command.List>

              {/* Footer */}
              <div className="flex items-center justify-between px-4 py-2 border-t border-white/[0.08] bg-white/[0.02]">
                <div className="flex items-center gap-3 text-[11px] text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <kbd className="px-1 py-0.5 rounded border border-white/[0.15] font-mono">↑↓</kbd>
                    <span>navigate</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <kbd className="px-1 py-0.5 rounded border border-white/[0.15] font-mono">⏎</kbd>
                    <span>select</span>
                  </span>
                </div>
                <div className="text-[11px] text-gray-500">
                  <kbd className="px-1.5 py-0.5 rounded border border-white/[0.15] font-mono mr-1">⌘K</kbd>
                  to toggle
                </div>
              </div>
            </Command>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

// ─── Item ───────────────────────────────────────────────────────────────────

function PaletteItem({
  action,
  onRun,
}: {
  action: CommandAction;
  onRun: () => void;
}) {
  const Icon = action.icon;
  // cmdk filters by the `value` prop — include label + keywords for search.
  const searchValue = [action.label, ...(action.keywords ?? [])].join(' ');

  return (
    <Command.Item
      value={searchValue}
      onSelect={onRun}
      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-200 cursor-pointer aria-selected:bg-white/[0.08] aria-selected:text-white data-[selected=true]:bg-white/[0.08] data-[selected=true]:text-white transition-colors"
    >
      {Icon && (
        <Icon size={16} strokeWidth={1.75} className="text-gray-400 flex-shrink-0" aria-hidden="true" />
      )}
      <div className="flex-1 min-w-0">
        <div className="truncate">{action.label}</div>
        {action.description && (
          <div className="text-[11px] text-gray-500 truncate">{action.description}</div>
        )}
      </div>
      {action.shortcut && (
        <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded border border-white/[0.12] text-[10px] font-mono text-gray-400">
          {action.shortcut}
        </kbd>
      )}
    </Command.Item>
  );
}

// ─── Search glyph ───────────────────────────────────────────────────────────

function SearchGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-gray-500 flex-shrink-0"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}
